import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  Platform,
  TextInput,
  Share,
  ActivityIndicator,
  Modal,
  Switch,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useToast } from '../contexts/ToastContext';
import { formatCurrency, calculateNetPayout } from '../services/paymentGateway';
import {
  getTournamentById,
  updateTournament as updateTournamentSupabase,
  addTournamentParticipant,
  updateTournamentParticipant as updateParticipantSupabase,
  createTournamentRound,
  createTournamentPairing,
  updateTournamentPairing as updatePairingSupabase,
  updateUser as updateUserSupabase,
} from '../services/database';

const PLATFORM_FEE_RATE = 0.1;

const TournamentControlScreen = ({ navigation, route }) => {
  const { tournamentId } = route.params;
  const { showToast } = useToast();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [participantName, setParticipantName] = useState('');
  const [participantSeed, setParticipantSeed] = useState('');
  const [announcementText, setAnnouncementText] = useState('');
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTotalRounds, setEditTotalRounds] = useState('');
  const [editTimeControl, setEditTimeControl] = useState('');
  const [editAllowPhones, setEditAllowPhones] = useState(true);
  
  // Round timer state
  const [roundTimeMinutes, setRoundTimeMinutes] = useState(0);
  const [roundTimeSeconds, setRoundTimeSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [roundStartTime, setRoundStartTime] = useState(null);
  const timerIntervalRef = useRef(null);
  const autoRefreshRef = useRef(null);

  useEffect(() => {
    fetchTournament();
    
    // Auto-refresh every 15 seconds for live updates
    autoRefreshRef.current = setInterval(() => {
      fetchTournament(true);
    }, 15000);
    
    return () => {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [tournamentId]);
  
  // Timer effect - counts up from round start
  useEffect(() => {
    if (timerRunning && roundStartTime) {
      timerIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - roundStartTime) / 1000);
        setRoundTimeMinutes(Math.floor(elapsed / 60));
        setRoundTimeSeconds(elapsed % 60);
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }
    
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [timerRunning, roundStartTime]);

  const participants = useMemo(
    () =>
      tournament?.participants?.items
        ? [...tournament.participants.items].sort(
            (a, b) => (a.seedNumber || 0) - (b.seedNumber || 0)
          )
        : [],
    [tournament]
  );

  const formatSettings = useMemo(() => {
    if (!tournament?.formatConfig) return {};
    try {
      return JSON.parse(tournament.formatConfig) || {};
    } catch (error) {
      console.warn('Invalid formatConfig JSON', error);
      return {};
    }
  }, [tournament]);

  const announcements = formatSettings.announcements || [];

  const participantById = useMemo(() => {
    const map = {};
    participants.forEach((p) => {
      if (p?.id) {
        map[p.id] = p;
      }
    });
    return map;
  }, [participants]);

  const rounds = useMemo(
    () =>
      tournament?.rounds?.items?.slice().sort((a, b) => a.roundNumber - b.roundNumber) || [],
    [tournament]
  );

  const pairings = useMemo(() => tournament?.pairings?.items || [], [tournament]);

  const fetchTournament = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const raw = await getTournamentById(tournamentId);
      // Map snake_case to camelCase for UI
      const tournamentData = {
        ...raw,
        clubId: raw.club_id,
        totalRounds: raw.total_rounds,
        currentRound: raw.current_round,
        allowPhones: raw.allow_phones,
        projectorCode: raw.projector_code,
        startTime: raw.start_time,
        isPaid: raw.is_paid,
        entryFee: raw.entry_fee,
        formatConfig: raw.format_config,
        participants: {
          items: (raw.participants || []).map(p => ({
            ...p,
            userId: p.user_id,
            guestName: p.guest_name,
            ratingSnapshot: p.rating_snapshot,
            seedNumber: p.seed_number,
            currentBoard: p.current_board,
            byeCount: p.bye_count,
            hasPaid: p.has_paid,
            paymentStatus: p.payment_status,
            amountPaid: p.amount_paid,
            paymentIntentId: p.payment_intent_id,
          })),
        },
        rounds: {
          items: (raw.rounds || []).map(r => ({
            ...r,
            roundNumber: r.round_number,
            startTime: r.start_time,
          })),
        },
        pairings: {
          items: (raw.pairings || []).map(p => ({
            ...p,
            roundNumber: p.round_number,
            boardNumber: p.board_number,
            tableLocation: p.table_location,
            whiteParticipantId: p.white_participant_id,
            blackParticipantId: p.black_participant_id,
            whiteParticipant: p.white_participant ? {
              id: p.white_participant.id,
              guestName: p.white_participant.guest_name,
            } : null,
            blackParticipant: p.black_participant ? {
              id: p.black_participant.id,
              guestName: p.black_participant.guest_name,
            } : null,
          })),
        },
      };
      setTournament(tournamentData);
      
      // Initialize edit form values
      if (tournamentData && !showEditModal) {
        setEditName(tournamentData.name || '');
        setEditDescription(tournamentData.description || '');
        setEditTotalRounds(String(tournamentData.totalRounds || 4));
        setEditAllowPhones(tournamentData.allowPhones !== false);
        
        // Parse time control from formatConfig
        try {
          const config = JSON.parse(tournamentData.formatConfig || '{}');
          setEditTimeControl(config.timeControl || '10+5');
        } catch {
          setEditTimeControl('10+5');
        }
      }
    } catch (error) {
      console.error('Error loading tournament control data:', error);
      if (!silent) {
        showToast('Unable to load tournament', 'error');
        navigation.goBack();
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Open edit modal with current values
  const openEditModal = () => {
    setEditName(tournament?.name || '');
    setEditDescription(tournament?.description || '');
    setEditTotalRounds(String(tournament?.totalRounds || 4));
    setEditAllowPhones(tournament?.allowPhones !== false);
    try {
      const config = JSON.parse(tournament?.formatConfig || '{}');
      setEditTimeControl(config.timeControl || '10+5');
    } catch {
      setEditTimeControl('10+5');
    }
    setShowEditModal(true);
  };
  
  // Save tournament edits
  const handleSaveEdits = async () => {
    try {
      setSubmitting(true);
      
      const currentConfig = tournament?.formatConfig ? JSON.parse(tournament.formatConfig) : {};
      const newConfig = {
        ...currentConfig,
        timeControl: editTimeControl,
      };
      
      await updateTournamentSupabase(tournamentId, {
        name: editName.trim(),
        description: editDescription.trim(),
        total_rounds: parseInt(editTotalRounds, 10) || 4,
        allow_phones: editAllowPhones,
        format_config: JSON.stringify(newConfig),
      });
      
      showToast('Tournament updated', 'success');
      setShowEditModal(false);
      await fetchTournament();
    } catch (error) {
      console.error('Error saving tournament edits:', error);
      showToast('Failed to save changes', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmAction = (title, message, onConfirm) => {
    Alert.alert(title, message, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Confirm',
        onPress: onConfirm,
      },
    ]);
  };

  const handleAddParticipant = async () => {
    if (!participantName.trim()) {
      showToast('Enter a player name', 'error');
      return;
    }

    try {
      setSubmitting(true);
      await addTournamentParticipant({
        tournament_id: tournamentId,
        guest_name: participantName.trim(),
        seed_number: parseInt(participantSeed || participants.length + 1, 10),
        score: 0,
        rating_snapshot: 1200,
        has_paid: tournament?.isPaid ? false : null,
        payment_status: tournament?.isPaid ? 'PENDING' : null,
      });
      setParticipantName('');
      setParticipantSeed('');
      showToast('Player added', 'success');
      fetchTournament();
    } catch (error) {
      console.error('Error adding participant:', error);
      showToast('Failed to add player', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddAnnouncement = async () => {
    if (!announcementText.trim()) {
      showToast('Announcement cannot be empty', 'error');
      return;
    }
    const nextAnnouncements = [
      {
        id: `ann_${Date.now()}`,
        message: announcementText.trim(),
        createdAt: new Date().toISOString(),
      },
      ...announcements,
    ].slice(0, 20);

    const nextSettings = {
      ...formatSettings,
      announcements: nextAnnouncements,
    };

    await persistFormatSettings(nextSettings);
    setAnnouncementText('');
    showToast('Announcement posted', 'success');
  };

  const updateTournamentState = async (updates, successMessage) => {
    try {
      setSubmitting(true);
      // Map camelCase to snake_case for Supabase
      const snakeUpdates = {};
      Object.entries(updates).forEach(([key, value]) => {
        const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
        snakeUpdates[snakeKey] = value;
      });
      await updateTournamentSupabase(tournamentId, snakeUpdates);
      if (successMessage) {
        showToast(successMessage, 'success');
      }
      await fetchTournament();
    } catch (error) {
      console.error('Error updating tournament:', error);
      showToast('Failed to update tournament', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const persistFormatSettings = async (nextSettings) => {
    try {
      setSubmitting(true);
      await updateTournamentSupabase(tournamentId, {
        format_config: JSON.stringify(nextSettings),
      });
      await fetchTournament();
    } catch (error) {
      console.error('Error updating announcements:', error);
      showToast('Failed to update announcements', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenRegistration = () => {
    updateTournamentState({ status: 'REGISTRATION' }, 'Registration opened');
  };

  const generateProjectorCode = () => Math.random().toString(36).substring(2, 8).toUpperCase();

  const ensureProjectorCode = async () => {
    if (tournament.projectorCode) {
      return tournament.projectorCode;
    }
    const newCode = generateProjectorCode();
    await updateTournamentState({ projectorCode: newCode }, 'Projector code ready');
    return newCode;
  };

  const handleShareProjectorLink = async () => {
    try {
      const code = await ensureProjectorCode();
      const link = `chessmaps://tournament/${tournamentId}/projector?code=${code}`;
      await Share.share({
        message: `Projector link for ${tournament.name}: ${link}\nCode: ${code}`,
      });
    } catch (error) {
      console.error('Error sharing projector link:', error);
      showToast('Unable to share projector link', 'error');
    }
  };

  const handleExportReport = async () => {
    const standingsText = participants
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .map(
        (player, index) =>
          `${index + 1}. ${player.guestName || 'Player'} — ${player.score?.toFixed(1) || '0.0'} pts`
      )
      .join('\n');
    const pairingsText = pairings
      .filter((pair) => pair.roundNumber === latestRound)
      .map(
        (pair) =>
          `Board ${pair.boardNumber}: ${pair.whiteParticipant?.guestName || 'White'} vs ${
            pair.blackParticipant?.guestName || 'Black'
          } (${pair.result || 'Pending'})`
      )
      .join('\n');

    try {
      await Share.share({
        message: `Tournament Report — ${tournament.name}\nRound ${latestRound}\n\nPairings:\n${pairingsText}\n\nStandings:\n${standingsText}`,
      });
    } catch (error) {
      console.error('Error sharing report:', error);
      showToast('Unable to share report', 'error');
    }
  };

  const ensureRoundExists = async (roundNumber) => {
    const exists = rounds.some((round) => round.roundNumber === roundNumber);
    if (exists) return;
    await createTournamentRound({
      tournament_id: tournamentId,
      round_number: roundNumber,
      status: 'IN_PROGRESS',
    });
  };

  const createPairingsForRound = async (roundNumber) => {
    const existing = pairings.filter((p) => p.roundNumber === roundNumber);
    if (existing.length > 0) {
      showToast(`Round ${roundNumber} already has pairings`, 'info');
      return;
    }

    if (participants.length < 2) {
      showToast('Not enough players to pair', 'error');
      return;
    }

    const sorted = [...participants].sort((a, b) => {
      const scoreDiff = (b.score || 0) - (a.score || 0);
      if (scoreDiff !== 0) return scoreDiff;
      return (a.seedNumber || 0) - (b.seedNumber || 0);
    });

    const pairingsToCreate = [];
    const queue = [...sorted];
    let byePlayer = null;

    if (queue.length % 2 === 1) {
      byePlayer = queue.pop();
    }

    while (queue.length > 0) {
      const white = queue.shift();
      const black = queue.shift();
      pairingsToCreate.push({
        whiteParticipantId: white.id,
        blackParticipantId: black?.id || null,
      });
    }

    try {
      await Promise.all(
        pairingsToCreate.map((pair, index) =>
          createTournamentPairing({
            tournament_id: tournamentId,
            round_number: roundNumber,
            board_number: index + 1,
            white_participant_id: pair.whiteParticipantId,
            black_participant_id: pair.blackParticipantId,
          })
        )
      );

      if (byePlayer) {
        await updateParticipantSupabase(byePlayer.id, {
          score: (byePlayer.score || 0) + 1,
          bye_count: (byePlayer.byeCount || 0) + 1,
        });
        showToast(`${byePlayer.guestName || 'Player'} receives a bye`, 'info');
      }

      showToast(`Round ${roundNumber} pairings generated`, 'success');
    } catch (error) {
      console.error('Error creating pairings:', error);
      showToast('Failed to create pairings', 'error');
    }
  };

  const recordResult = async (pairing, result) => {
    if (!pairing) return;
    const white = participantById[pairing.whiteParticipantId];
    const black = participantById[pairing.blackParticipantId];

    const deltaMap = {
      WHITE_WIN: [1, 0],
      BLACK_WIN: [0, 1],
      DRAW: [0.5, 0.5],
      DOUBLE_FORFEIT: [0, 0],
      BYE: [1, 0],
      UNREPORTED: [0, 0],
    };

    const [whiteDelta, blackDelta] = deltaMap[result] || [0, 0];
    const [prevWhiteDelta, prevBlackDelta] = deltaMap[pairing.result] || [0, 0];
    const whiteDiff = whiteDelta - prevWhiteDelta;
    const blackDiff = blackDelta - prevBlackDelta;

    try {
      setSubmitting(true);
      await updatePairingSupabase(pairing.id, { result });

      const updatePromises = [];
      if (white && whiteDiff !== 0) {
        updatePromises.push(
          updateParticipantSupabase(white.id, {
            score: (white.score || 0) + whiteDiff,
          })
        );
      }
      if (black && blackDiff !== 0) {
        updatePromises.push(
          updateParticipantSupabase(black.id, {
            score: (black.score || 0) + blackDiff,
          })
        );
      }
      await Promise.all(updatePromises);
      showToast('Result recorded', 'success');
      fetchTournament();
    } catch (error) {
      console.error('Error recording result:', error);
      showToast('Failed to record result', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTogglePayment = async (participant) => {
    if (!tournament.isPaid) return;
    try {
      setSubmitting(true);
      await updateParticipantSupabase(participant.id, {
        has_paid: !participant.hasPaid,
        payment_status: !participant.hasPaid ? 'PAID' : 'PENDING',
        amount_paid: !participant.hasPaid
          ? participant.amountPaid || tournament.entryFee || 0
          : null,
      });
      fetchTournament();
    } catch (error) {
      console.error('Error updating payment status:', error);
      showToast('Unable to update payment status', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const formatAnnouncementTime = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return '';
    }
  };

  const handleStartTournament = () => {
    if (participants.length < 2) {
      showToast('Add at least 2 players', 'error');
      return;
    }
    confirmAction('Start tournament?', 'Registration will close and round 1 pairings will be generated.', async () => {
      try {
        setSubmitting(true);
        const startTime = new Date().toISOString();
        await updateTournamentState({ status: 'IN_PROGRESS', currentRound: 1, startTime });
        await ensureRoundExists(1);
        await createPairingsForRound(1);
        
        // Start the round timer
        setRoundStartTime(Date.now());
        setTimerRunning(true);
        setRoundTimeMinutes(0);
        setRoundTimeSeconds(0);
        
        await fetchTournament();
        showToast('Tournament started! Round 1 is live.', 'success');
      } catch (error) {
        console.error('Error starting tournament:', error);
        showToast('Failed to start tournament', 'error');
      } finally {
        setSubmitting(false);
      }
    });
  };
  
  // Start/stop round timer manually
  const toggleTimer = () => {
    if (timerRunning) {
      setTimerRunning(false);
    } else {
      if (!roundStartTime) {
        setRoundStartTime(Date.now());
      }
      setTimerRunning(true);
    }
  };
  
  // Reset timer for new round
  const resetTimer = () => {
    setRoundStartTime(Date.now());
    setRoundTimeMinutes(0);
    setRoundTimeSeconds(0);
    setTimerRunning(true);
  };

  const handleAdvanceRound = () => {
    const nextRound = (tournament.currentRound || 0) + 1;
    if (tournament.totalRounds && nextRound > tournament.totalRounds) {
      showToast('All scheduled rounds already completed', 'info');
      return;
    }

    confirmAction(
      'Advance round?',
      `Generate pairings for round ${nextRound}?`,
      async () => {
        try {
          setSubmitting(true);
          await ensureRoundExists(nextRound);
          await createPairingsForRound(nextRound);
          await updateTournamentState({ currentRound: nextRound, status: 'IN_PROGRESS' }, `Round ${nextRound} ready`);
          
          // Reset timer for new round
          resetTimer();
        } finally {
          setSubmitting(false);
        }
      }
    );
  };

  const handleLockTournament = () => {
    confirmAction(
      'Complete tournament?',
      'Standings will lock and rating updates will trigger.',
      async () => {
        await applyRatingUpdates();
        await updateTournamentState({ status: 'COMPLETED' }, 'Tournament completed');
      }
    );
  };

  const applyRatingUpdates = async () => {
    const roundsPlayed = tournament.totalRounds || rounds.length || 1;
    const kFactor = 16;
    try {
      const updates = participants
        .filter((player) => player.userId)
        .map((player) => {
          const baseRating = player.ratingSnapshot || 1200;
          const expected = roundsPlayed / 2;
          const performance = (player.score || 0) - expected;
          const delta = Math.round(performance * kFactor);
          const nextRating = Math.max(100, baseRating + delta);
          return updateUserSupabase(player.userId, {
            rating: nextRating,
          }
          );
        });

      await Promise.all(updates);
    } catch (error) {
      console.error('Error updating ratings:', error);
      showToast('Ratings could not be updated automatically', 'error');
    }
  };

  if (loading || !tournament) {
    return (
      <View style={styles.centered}>
        <LoadingAnimation size={525} />
      </View>
    );
  }

  const roundList =
    tournament.rounds?.items?.sort((a, b) => a.roundNumber - b.roundNumber) || [];

  const status = tournament.status;
  const canStart = status === 'REGISTRATION' && participants.length >= 2;
  const canAdvance = status === 'IN_PROGRESS' && participants.length >= 2;
  const latestRound = tournament.currentRound || 0;
  const currentPairings = pairings.filter((p) => p.roundNumber === latestRound);
  const paidParticipants = participants.filter((player) => player.hasPaid);
  const grossRevenue = paidParticipants.reduce(
    (sum, player) => sum + (player.amountPaid || tournament.entryFee || 0),
    0
  );
  const estimatedProcessorFees =
    grossRevenue > 0 ? grossRevenue * 0.029 + paidParticipants.length * 0.3 : 0;
  const estimatedPlatformFee = grossRevenue * PLATFORM_FEE_RATE;
  const netRevenue = calculateNetPayout(
    grossRevenue,
    estimatedProcessorFees,
    estimatedPlatformFee
  );

  // Format time display
  const formatTime = (mins, secs) => {
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <ScrollView style={styles.container}>
      <View style={styles.hero}>
        <View style={styles.heroTopRow}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Icon name="arrow-back" size={24} color="white" />
          </TouchableOpacity>
          {(status === 'DRAFT' || status === 'REGISTRATION') && (
            <TouchableOpacity onPress={openEditModal} style={styles.editButton}>
              <Icon name="edit" size={20} color="white" />
              <Text style={styles.editButtonText}>Edit</Text>
            </TouchableOpacity>
          )}
        </View>
        <Text style={styles.heroTitle}>{tournament.name}</Text>
        <Text style={styles.heroSubtitle}>
          Round {latestRound}/{tournament.totalRounds || '?'} • {tournament.type.replace('_', ' ')}
        </Text>
        <View style={styles.heroStatusRow}>
          <View style={[styles.statusBadge, styles[`status${status}`]]}>
            <Text style={styles.statusText}>{status.replace('_', ' ')}</Text>
          </View>
          {status === 'IN_PROGRESS' && (
            <View style={styles.timerContainer}>
              <Icon name="timer" size={16} color="#94EB98" />
              <Text style={styles.timerText}>{formatTime(roundTimeMinutes, roundTimeSeconds)}</Text>
              <TouchableOpacity onPress={toggleTimer} style={styles.timerButton}>
                <Icon name={timerRunning ? 'pause' : 'play-arrow'} size={18} color="white" />
              </TouchableOpacity>
              <TouchableOpacity onPress={resetTimer} style={styles.timerButton}>
                <Icon name="replay" size={18} color="white" />
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Director Actions</Text>
        {status === 'DRAFT' && (
          <TouchableOpacity
            style={styles.primaryButton}
            onPress={handleOpenRegistration}
          >
            <Icon name="how-to-reg" size={20} color="white" />
            <Text style={styles.primaryButtonText}>Open Registration</Text>
          </TouchableOpacity>
        )}

        {status === 'REGISTRATION' && (
          <TouchableOpacity
            style={[styles.primaryButton, !canStart && styles.primaryButtonDisabled]}
            onPress={handleStartTournament}
            disabled={!canStart || submitting}
          >
            {submitting ? (
              <ActivityIndicator size="small" color="white" />
            ) : (
              <>
                <Icon name="play-arrow" size={20} color="white" />
                <Text style={styles.primaryButtonText}>
                  Start Tournament
                </Text>
              </>
            )}
          </TouchableOpacity>
        )}

        {status === 'IN_PROGRESS' && (
          <>
            <TouchableOpacity
              style={[styles.primaryButton, !canAdvance && styles.primaryButtonDisabled]}
              onPress={handleAdvanceRound}
              disabled={!canAdvance || submitting}
            >
              {submitting ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <>
                  <Icon name="skip-next" size={20} color="white" />
                  <Text style={styles.primaryButtonText}>Advance Round</Text>
                </>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.dangerButton}
              onPress={handleLockTournament}
              disabled={submitting}
            >
              <Icon name="lock" size={20} color="white" />
              <Text style={styles.dangerButtonText}>Lock & Publish</Text>
            </TouchableOpacity>
          </>
        )}

        {status === 'COMPLETED' && (
          <Text style={styles.helperText}>
            Tournament completed. Rating updates run in the background.
          </Text>
        )}

        <TouchableOpacity
          style={styles.secondaryButton}
          onPress={handleShareProjectorLink}
        >
          <Icon name="share" size={20} color="#333" />
          <Text style={styles.secondaryButtonText}>Share Projector Link</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.secondaryButton}
          onPress={handleExportReport}
        >
          <Icon name="download" size={20} color="#333" />
          <Text style={styles.secondaryButtonText}>Export Round Report</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Participants ({participants.length})</Text>
        </View>
        {participants.length === 0 ? (
          <View style={styles.emptyState}>
            <Icon name="people" size={48} color="#ccc" />
            <Text style={styles.emptyTitle}>No players yet</Text>
            <Text style={styles.emptySubtitle}>Add club members or guests below.</Text>
          </View>
        ) : (
          participants.map((player) => (
            <View key={player.id} style={styles.participantRow}>
              <View style={styles.avatar}>
                <Text style={styles.avatarText}>
                  {(player.guestName || 'P')[0].toUpperCase()}
                </Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.playerName}>
                  {player.guestName || `Player ${player.seedNumber || ''}`}
                </Text>
                <Text style={styles.playerSubtext}>
                  Score {player.score?.toFixed(1) || '0.0'} • Board {player.currentBoard || '-'}
                </Text>
              </View>
              <Text style={styles.seedBadge}>#{player.seedNumber || '-'}</Text>
              {tournament.isPaid && (
                <TouchableOpacity
                  style={[
                    styles.paymentChip,
                    player.hasPaid && styles.paymentChipPaid,
                  ]}
                  onPress={() => handleTogglePayment(player)}
                >
                  <Text
                    style={[
                      styles.paymentChipText,
                      player.hasPaid && styles.paymentChipTextPaid,
                    ]}
                  >
                    {player.hasPaid ? 'Paid' : 'Mark Paid'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          ))
        )}
        {status === 'REGISTRATION' || status === 'DRAFT' ? (
          <View style={styles.addPlayerCard}>
            <TextInput
              style={styles.input}
              value={participantName}
              onChangeText={setParticipantName}
              placeholder="Player name"
            />
            <TextInput
              style={styles.input}
              value={participantSeed}
              onChangeText={setParticipantSeed}
              placeholder="Seed / rating"
              keyboardType="number-pad"
            />
            <TouchableOpacity
              style={styles.secondaryButton}
              onPress={handleAddParticipant}
            >
              <Icon name="person-add" size={18} color="#333" />
              <Text style={styles.secondaryButtonText}>Add Player</Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </View>

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Announcements</Text>
          <Text style={styles.helperText}>{announcements.length} posted</Text>
        </View>
        <View style={styles.addPlayerCard}>
          <TextInput
            style={styles.input}
            placeholder="Share updates with players..."
            value={announcementText}
            onChangeText={setAnnouncementText}
          />
          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={handleAddAnnouncement}
            disabled={submitting}
          >
            <Icon name="campaign" size={18} color="#333" />
            <Text style={styles.secondaryButtonText}>Post Announcement</Text>
          </TouchableOpacity>
        </View>
        {announcements.length === 0 ? (
          <Text style={styles.helperText}>No announcements yet.</Text>
        ) : (
          announcements.slice(0, 5).map((announcement) => (
            <View key={announcement.id} style={styles.announcementRow}>
              <View style={styles.announcementBadge}>
                <Icon name="campaign" size={16} color="#fff" />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.playerName}>{announcement.message}</Text>
                <Text style={styles.playerSubtext}>
                  {formatAnnouncementTime(announcement.createdAt)}
                </Text>
              </View>
            </View>
          ))
        )}
      </View>

      {tournament.isPaid && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Payments</Text>
          <View style={styles.paymentRow}>
            <View>
              <Text style={styles.paymentLabel}>Gross collected</Text>
              <Text style={styles.paymentValue}>{formatCurrency(grossRevenue)}</Text>
            </View>
            <View>
              <Text style={styles.paymentLabel}>Net to club</Text>
              <Text style={styles.paymentValue}>{formatCurrency(netRevenue)}</Text>
            </View>
          </View>
          <Text style={styles.helperText}>
            Includes estimated processor (${estimatedProcessorFees.toFixed(2)}) & platform fees (
            {Math.round(PLATFORM_FEE_RATE * 100)}%).
          </Text>
          <View style={styles.paymentRow}>
            <Text style={styles.paymentLabel}>
              {paidParticipants.length}/{participants.length} players paid
            </Text>
          </View>
        </View>
      )}

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>
            {latestRound ? `Round ${latestRound} Pairings` : 'No pairings yet'}
          </Text>
          <TouchableOpacity
            onPress={() =>
              navigation.navigate('TournamentProjector', { tournament })
            }
          >
            <Text style={styles.link}>Projector Mode</Text>
          </TouchableOpacity>
        </View>
        {currentPairings.length === 0 ? (
          <View style={styles.emptyState}>
            <Icon name="repeat" size={48} color="#ccc" />
            <Text style={styles.emptyTitle}>No pairings posted</Text>
            <Text style={styles.emptySubtitle}>
              Generate pairings to populate boards.
            </Text>
          </View>
        ) : (
          currentPairings.map((pair) => (
            <View key={pair.id} style={styles.pairingRow}>
              <View style={styles.boardBadge}>
                <Text style={styles.boardBadgeText}>{pair.boardNumber}</Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.playerName}>
                  {pair.whiteParticipant?.guestName || 'White TBD'}
                </Text>
                <Text style={styles.playerName}>
                  {pair.blackParticipant?.guestName || 'Black TBD'}
                </Text>
                <View style={styles.resultButtons}>
                  {['WHITE_WIN', 'DRAW', 'BLACK_WIN'].map((outcome) => (
                    <TouchableOpacity
                      key={outcome}
                      style={[
                        styles.resultButton,
                        pair.result === outcome && styles.resultButtonActive,
                      ]}
                      onPress={() => recordResult(pair, outcome)}
                    >
                      <Text
                        style={[
                          styles.resultButtonText,
                          pair.result === outcome && styles.resultButtonTextActive,
                        ]}
                      >
                        {outcome === 'WHITE_WIN' ? 'White' : outcome === 'BLACK_WIN' ? 'Black' : 'Draw'}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </View>
          ))
        )}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Rounds</Text>
        {rounds.length === 0 ? (
          <View style={styles.emptyState}>
            <Icon name="repeat" size={48} color="#ccc" />
            <Text style={styles.emptyTitle}>No rounds yet</Text>
          </View>
        ) : (
          rounds.map((round) => (
            <View key={round.id} style={styles.roundRow}>
              <View style={styles.roundInfo}>
                <Text style={styles.roundTitle}>Round {round.roundNumber}</Text>
                <Text style={styles.roundSubtitle}>{round.status}</Text>
              </View>
            </View>
          ))
        )}
      </View>

      <View style={{ height: 40 }} />
      
      {/* Edit Tournament Modal */}
      <Modal
        visible={showEditModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowEditModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Edit Tournament</Text>
              <TouchableOpacity onPress={() => setShowEditModal(false)}>
                <Icon name="close" size={24} color="#333" />
              </TouchableOpacity>
            </View>
            
            <ScrollView style={styles.modalBody}>
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Tournament Name</Text>
                <TextInput
                  style={styles.formInput}
                  value={editName}
                  onChangeText={setEditName}
                  placeholder="Enter tournament name"
                />
              </View>
              
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Description</Text>
                <TextInput
                  style={[styles.formInput, styles.formInputMultiline]}
                  value={editDescription}
                  onChangeText={setEditDescription}
                  placeholder="Enter description"
                  multiline
                  numberOfLines={3}
                />
              </View>
              
              <View style={styles.formRow}>
                <View style={[styles.formField, { flex: 1 }]}>
                  <Text style={styles.formLabel}>Total Rounds</Text>
                  <TextInput
                    style={styles.formInput}
                    value={editTotalRounds}
                    onChangeText={setEditTotalRounds}
                    placeholder="4"
                    keyboardType="number-pad"
                  />
                </View>
                <View style={[styles.formField, { flex: 1, marginLeft: 12 }]}>
                  <Text style={styles.formLabel}>Time Control</Text>
                  <TextInput
                    style={styles.formInput}
                    value={editTimeControl}
                    onChangeText={setEditTimeControl}
                    placeholder="10+5"
                  />
                </View>
              </View>
              
              <View style={styles.formSwitchRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.formLabel}>Allow Player Devices</Text>
                  <Text style={styles.formHint}>
                    Players can view pairings and report results on their phones
                  </Text>
                </View>
                <Switch
                  value={editAllowPhones}
                  onValueChange={setEditAllowPhones}
                  trackColor={{ false: '#E0E0E0', true: '#94EB98' }}
                />
              </View>
            </ScrollView>
            
            <View style={styles.modalFooter}>
              <TouchableOpacity
                style={styles.modalCancelButton}
                onPress={() => setShowEditModal(false)}
              >
                <Text style={styles.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalSaveButton, submitting && styles.modalSaveButtonDisabled]}
                onPress={handleSaveEdits}
                disabled={submitting}
              >
                {submitting ? (
                  <ActivityIndicator size="small" color="white" />
                ) : (
                  <Text style={styles.modalSaveText}>Save Changes</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  hero: {
    backgroundColor: '#111827',
    paddingTop: Platform.OS === 'ios' ? 60 : 30,
    paddingBottom: 24,
    paddingHorizontal: 20,
  },
  heroTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  editButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  heroStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 12,
  },
  timerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(255,255,255,0.1)',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  timerText: {
    color: '#94EB98',
    fontSize: 18,
    fontWeight: 'bold',
    fontVariant: ['tabular-nums'],
  },
  timerButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroTitle: {
    fontSize: 26,
    fontWeight: 'bold',
    color: 'white',
  },
  heroSubtitle: {
    fontSize: 15,
    color: '#9CA3AF',
    marginTop: 4,
  },
  section: {
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: Platform.OS === 'ios' ? 0.08 : 0.2,
    shadowRadius: 12,
    elevation: 4,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 12,
  },
  helperText: {
    fontSize: 13,
    color: '#777',
    marginBottom: 12,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#2E7D32',
    padding: 14,
    borderRadius: 12,
    marginBottom: 12,
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  primaryButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
  },
  secondaryButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
  },
  dangerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#C62828',
    padding: 12,
    borderRadius: 12,
    marginTop: 4,
  },
  dangerButtonText: {
    color: 'white',
    fontSize: 15,
    fontWeight: '600',
  },
  roundRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  roundInfo: {
    flex: 1,
  },
  roundTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  roundSubtitle: {
    fontSize: 13,
    color: '#777',
  },
  roundButton: {
    padding: 6,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  link: {
    color: '#2E7D32',
    fontWeight: '600',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#777',
    marginTop: 8,
  },
  statusBadge: {
    alignSelf: 'flex-start',
    marginTop: 12,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  statusText: {
    color: 'white',
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  statusDRAFT: {
    backgroundColor: '#9E9E9E',
  },
  statusREGISTRATION: {
    backgroundColor: '#1976D2',
  },
  statusIN_PROGRESS: {
    backgroundColor: '#2E7D32',
  },
  statusCOMPLETED: {
    backgroundColor: '#6D4C41',
  },
  statusCANCELLED: {
    backgroundColor: '#C62828',
  },
  participantRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#E8F5E9',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#2E7D32',
  },
  seedBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: '#F3F4F6',
    fontWeight: '600',
    color: '#555',
  },
  addPlayerCard: {
    marginTop: 16,
    padding: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 12,
    gap: 10,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 10,
    padding: 12,
    fontSize: 15,
    backgroundColor: '#fff',
  },
  pairingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f3f3',
  },
  boardBadge: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#E3F2FD',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  boardBadgeText: {
    fontWeight: '700',
    color: '#1976D2',
  },
  announcementRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  announcementBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#1976D2',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  paymentRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
  },
  paymentLabel: {
    fontSize: 14,
    color: '#666',
  },
  paymentValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111',
  },
  paymentChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#ddd',
    marginLeft: 8,
  },
  paymentChipPaid: {
    borderColor: '#2E7D32',
    backgroundColor: '#E8F5E9',
  },
  paymentChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#555',
  },
  paymentChipTextPaid: {
    color: '#2E7D32',
  },
  resultButtons: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  resultButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#fff',
  },
  resultButtonActive: {
    backgroundColor: '#E8F5E9',
    borderColor: '#2E7D32',
  },
  resultButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#555',
  },
  resultButtonTextActive: {
    color: '#2E7D32',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  loadingText: {
    marginTop: 10,
    color: '#666',
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: 'white',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '85%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  modalBody: {
    padding: 20,
  },
  modalFooter: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    gap: 12,
  },
  modalCancelButton: {
    flex: 1,
    padding: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#ddd',
    alignItems: 'center',
  },
  modalCancelText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
  },
  modalSaveButton: {
    flex: 1,
    padding: 14,
    borderRadius: 12,
    backgroundColor: '#2E7D32',
    alignItems: 'center',
  },
  modalSaveButtonDisabled: {
    opacity: 0.6,
  },
  modalSaveText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
  formField: {
    marginBottom: 16,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#555',
    marginBottom: 8,
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 10,
    padding: 12,
    fontSize: 15,
    backgroundColor: '#fff',
    color: '#333',
  },
  formInputMultiline: {
    height: 80,
    textAlignVertical: 'top',
  },
  formRow: {
    flexDirection: 'row',
  },
  formSwitchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    marginTop: 8,
  },
  formHint: {
    fontSize: 12,
    color: '#888',
    marginTop: 4,
  },
});

export default TournamentControlScreen;

